import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import { useLocation } from "react-router-dom";
import {
  TutorialStep,
  TutorialContext,
  getTutorialSteps,
  getTabDataAttribute,
  getActiveTabFromDOM,
  isSidebarStep
} from "./tutorial";

const STORAGE_KEY = "qqoq_header_onboarding_seen";

// Re-export types for backward compatibility
export type { TutorialContext } from "./tutorial";

interface OnboardingTutorialProps {
  forceShow?: boolean;
  onClose?: () => void;
  context?: TutorialContext;
}

export function OnboardingTutorial({ forceShow = false, onClose, context = "public" }: OnboardingTutorialProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isWaitingForSidebar, setIsWaitingForSidebar] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const previousSidebarState = useRef<boolean | null>(null);
  
  // Get current pathname
  const location = useLocation();
  const pathname = location.pathname;

  // Access sidebar context (may be null if not in SidebarProvider)
  let sidebarContext: { setOpen?: (open: boolean) => void; open?: boolean; isMobile?: boolean; setOpenMobile?: (open: boolean) => void } = {};
  try {
    sidebarContext = useSidebar();
  } catch {
    // Not in a SidebarProvider, ignore
  }

  // Get the tab data attribute for the current page
  const tabDataAttribute = getTabDataAttribute(context, pathname);

  // Observe tab changes for pages with tabs
  useEffect(() => {
    if (!tabDataAttribute) {
      setActiveTab(undefined);
      return;
    }
    
    const checkTab = () => {
      const newTab = getActiveTabFromDOM(tabDataAttribute);
      setActiveTab(prev => {
        if (prev !== newTab) {
          // Reset to first step when tab changes
          setCurrentStep(0);
          return newTab;
        }
        return prev;
      });
    };

    // Check immediately
    checkTab();

    // Observe DOM mutations to detect tab changes
    const observer = new MutationObserver(checkTab);
    const tabsElement = document.querySelector(`[${tabDataAttribute}]`);
    if (tabsElement) {
      observer.observe(tabsElement, { attributes: true, attributeFilter: [tabDataAttribute] });
    }

    // Also listen for clicks on tabs
    const handleClick = () => setTimeout(checkTab, 50);
    document.addEventListener('click', handleClick);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleClick);
    };
  }, [pathname, tabDataAttribute]);

  // Get tutorial steps based on context, pathname, and active tab
  const allSteps = getTutorialSteps(context, pathname, activeTab);
  const tutorialSteps = sidebarContext.isMobile 
    ? allSteps.filter(step => !isSidebarStep(step))
    : allSteps;

  // Reset currentStep if index exceeds number of steps (mobile/desktop change or tab change)
  useEffect(() => {
    if (currentStep >= tutorialSteps.length && tutorialSteps.length > 0) {
      setCurrentStep(0);
    }
  }, [currentStep, tutorialSteps.length]);

  // Handle forceShow
  useEffect(() => {
    if (forceShow) {
      setCurrentStep(0);
      setIsVisible(true);
    }
  }, [forceShow]);

  // Auto-show tutorial once (first visit) - only for public context
  useEffect(() => {
    if (forceShow) return;
    if (context !== "public") return;
    
    const hasSeen = localStorage.getItem(STORAGE_KEY);
    
    if (!hasSeen) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        localStorage.setItem(STORAGE_KEY, "true");
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [forceShow, context]);

  // Open/close sidebar based on step
  useEffect(() => {
    if (!isVisible) return;
    
    const step = tutorialSteps[currentStep];
    if (!step) return;

    const needsSidebar = isSidebarStep(step);
    
    if (needsSidebar && sidebarContext.setOpen) {
      // Save current state if not already saved
      if (previousSidebarState.current === null) {
        previousSidebarState.current = sidebarContext.open ?? false;
      }
      
      // Indicate waiting for sidebar to open
      setIsWaitingForSidebar(true);
      setTargetRect(null);
      
      // Open sidebar
      sidebarContext.setOpen(true);
      if (sidebarContext.isMobile && sidebarContext.setOpenMobile) {
        sidebarContext.setOpenMobile(true);
      }
      
      // Wait for sidebar animation to complete
      const timer = setTimeout(() => {
        setIsWaitingForSidebar(false);
      }, 400);
      
      return () => clearTimeout(timer);
    } else {
      setIsWaitingForSidebar(false);
    }
  }, [currentStep, isVisible, tutorialSteps, sidebarContext.setOpen, sidebarContext.setOpenMobile, sidebarContext.isMobile, sidebarContext.open]);

  // Restore sidebar state on close
  const restoreSidebarState = useCallback(() => {
    if (previousSidebarState.current !== null && sidebarContext.setOpen) {
      sidebarContext.setOpen(previousSidebarState.current);
      previousSidebarState.current = null;
    }
  }, [sidebarContext]);

  // Function to switch tabs programmatically based on target selector
  const switchToRequiredTab = useCallback((targetSelector: string): boolean => {
    // Detect if target is related to a specific tab based on naming convention
    let requiredTab: string | null = null;
    let tabTriggerSelector: string | null = null;
    
    // Mission tabs: voice-* targets need "voice" tab, text-* targets need "text" tab
    if (targetSelector.includes('voice-')) {
      requiredTab = 'voice';
      tabTriggerSelector = '[data-onboarding="mission-tab-voice"]';
    } else if (targetSelector.includes('text-mission-')) {
      requiredTab = 'text';
      tabTriggerSelector = '[data-onboarding="mission-tab-text"]';
    }
    // Support tabs: ticket-form-*, ticket-category-*, ticket-subject-*, etc. need "new" tab
    else if (targetSelector.includes('ticket-form') || 
             targetSelector.includes('ticket-category') || 
             targetSelector.includes('ticket-subject') || 
             targetSelector.includes('ticket-description') ||
             targetSelector.includes('ticket-moderation') ||
             targetSelector.includes('ticket-attachments') ||
             targetSelector.includes('ticket-add-file') ||
             targetSelector.includes('ticket-files-list') ||
             targetSelector.includes('ticket-participants') ||
             targetSelector.includes('ticket-add-participant') ||
             targetSelector.includes('ticket-submit')) {
      requiredTab = 'new';
      tabTriggerSelector = '[data-onboarding="support-tab-new"]';
    }
    // Support tabs: ticket-list-* targets need "tickets" tab
    else if (targetSelector.includes('ticket-list-')) {
      requiredTab = 'tickets';
      tabTriggerSelector = '[data-onboarding="support-tab-tickets"]';
    }
    // Support tabs: ticket-detail-*, ticket-email-* targets need "detail" tab
    else if (targetSelector.includes('ticket-detail-') || targetSelector.includes('ticket-email-')) {
      requiredTab = 'detail';
      tabTriggerSelector = '[data-onboarding="support-tab-detail"]';
    }
    // Calendrier tabs
    else if (targetSelector.includes('calendrier-settings-')) {
      requiredTab = 'settings';
      tabTriggerSelector = '[data-onboarding="calendrier-tab-settings"]';
    }
    
    if (!requiredTab || !tabTriggerSelector) return false;
    
    // Find the tab trigger and click it if it's not already active
    const tabTrigger = document.querySelector(tabTriggerSelector) as HTMLElement;
    if (tabTrigger) {
      const isActive = tabTrigger.getAttribute('data-state') === 'active';
      if (!isActive) {
        // Use dispatchEvent with bubbling to properly trigger React's synthetic events
        const mouseDownEvent = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        
        tabTrigger.dispatchEvent(mouseDownEvent);
        tabTrigger.dispatchEvent(mouseUpEvent);
        tabTrigger.dispatchEvent(clickEvent);
        
        return true; // Indicate that we switched tabs
      }
    }
    
    return false;
  }, []);

  // Update target element position
  const updateTargetPosition = useCallback(() => {
    if (!isVisible || isWaitingForSidebar) return;
    
    const step = tutorialSteps[currentStep];
    if (!step) return;
    
    const findElement = (attempts = 0, tabSwitched = false) => {
      let element = document.querySelector(step.target);
      
      // If element not found and we haven't tried switching tabs yet, try to switch
      if (!element && !tabSwitched && attempts === 0) {
        const didSwitch = switchToRequiredTab(step.target);
        if (didSwitch) {
          // Wait for tab content to render then try again
          setTimeout(() => findElement(0, true), 350);
          return;
        }
      }
      
      if (element) {
        // Scroll element into view if needed
        const rect = element.getBoundingClientRect();
        const isInViewport = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth
        );
        
        if (!isInViewport && attempts === 0) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Wait for scroll to complete before getting rect
          setTimeout(() => findElement(attempts + 1, tabSwitched), 400);
          return;
        }
        
        // Check element is visible (not off-screen)
        const updatedRect = element.getBoundingClientRect();
        if (updatedRect.width > 0 && updatedRect.height > 0) {
          setTargetRect(updatedRect);
          return;
        }
      }
      
      // Retry up to 5 times with increasing delay
      if (attempts < 5) {
        setTimeout(() => findElement(attempts + 1, tabSwitched), 150);
      }
    };
    
    findElement();
  }, [currentStep, isVisible, isWaitingForSidebar, tutorialSteps, switchToRequiredTab]);

  useEffect(() => {
    if (isWaitingForSidebar) return;
    
    // Delay to allow sidebar to open
    const timer = setTimeout(updateTargetPosition, 200);
    
    // Debounced scroll handler for smooth updates
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateTargetPosition, 50);
    };
    
    window.addEventListener("resize", updateTargetPosition);
    window.addEventListener("scroll", handleScroll, true); // 'true' captures scroll on all elements
    
    return () => {
      clearTimeout(timer);
      clearTimeout(scrollTimeout);
      window.removeEventListener("resize", updateTargetPosition);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [updateTargetPosition, isWaitingForSidebar]);

  const handleNext = useCallback((e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (currentStep < tutorialSteps.length - 1) {
      setIsTransitioning(true);
      setCurrentStep(prev => prev + 1);
      // Allow transition time before re-enabling close
      setTimeout(() => setIsTransitioning(false), 500);
    } else {
      handleCloseInternal();
    }
  }, [currentStep, tutorialSteps.length]);

  const handlePrevious = useCallback((e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (currentStep > 0) {
      setIsTransitioning(true);
      setCurrentStep(prev => prev - 1);
      // Allow transition time before re-enabling close
      setTimeout(() => setIsTransitioning(false), 500);
    }
  }, [currentStep]);

  const handleCloseInternal = useCallback(() => {
    restoreSidebarState();
    setIsVisible(false);
    onClose?.();
  }, [restoreSidebarState, onClose]);

  const handleClose = useCallback((e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    // Don't close during transition between steps
    if (isTransitioning) return;
    handleCloseInternal();
  }, [handleCloseInternal, isTransitioning]);

  const step = tutorialSteps[currentStep];
  
  // Don't render if not visible, no target rect, or invalid step
  if (!isVisible || !targetRect || !step) return null;

  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    const padding = 12;
    const tooltipWidth = 280;
    const tooltipHeight = 280; // Approximate max height of tooltip with all content
    
    // Ensure target is in visible area, otherwise center the tooltip
    const targetInViewport = (
      targetRect.top >= -50 &&
      targetRect.bottom <= window.innerHeight + 50 &&
      targetRect.left >= -50 &&
      targetRect.right <= window.innerWidth + 50
    );
    
    if (!targetInViewport) {
      // Center the tooltip when target is off-screen
      return {
        position: "fixed",
        top: Math.max(padding, (window.innerHeight - tooltipHeight) / 2),
        left: Math.max(padding, (window.innerWidth - tooltipWidth) / 2),
        width: tooltipWidth,
        maxHeight: `calc(100vh - ${padding * 2}px)`,
        overflowY: "auto" as const,
        zIndex: 10001
      };
    }
    
    let left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    
    // Adjust if tooltip exceeds left edge
    if (left < padding) {
      left = padding;
    }
    
    // Adjust if tooltip exceeds right edge
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = window.innerWidth - tooltipWidth - padding;
    }

    // Determine if tooltip should be above or below target
    const spaceBelow = window.innerHeight - targetRect.bottom - 12;
    const spaceAbove = targetRect.top - 12;
    const showAbove = spaceBelow < tooltipHeight && spaceAbove > spaceBelow;

    // Calculate top position - ensure it stays within viewport
    let top: number;
    if (showAbove) {
      top = Math.max(padding, targetRect.top - tooltipHeight - 12);
    } else {
      top = targetRect.bottom + 12;
    }
    
    // Final boundary check to ensure tooltip is always visible
    if (top < padding) {
      top = padding;
    }
    if (top + tooltipHeight > window.innerHeight - padding) {
      top = window.innerHeight - tooltipHeight - padding;
    }

    return {
      position: "fixed",
      top,
      left,
      width: tooltipWidth,
      maxHeight: `calc(100vh - ${padding * 2}px)`,
      overflowY: "auto" as const,
      zIndex: 10001
    };
  };

  // Create mask with hole for spotlight effect
  const getMaskStyle = (): React.CSSProperties => {
    const padding = 8;
    const top = targetRect.top - padding;
    const left = targetRect.left - padding;
    const width = targetRect.width + padding * 2;
    const height = targetRect.height + padding * 2;
    
    return {
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: `
        linear-gradient(to bottom, 
          rgba(0,0,0,0.4) 0%, 
          rgba(0,0,0,0.4) ${top}px, 
          transparent ${top}px, 
          transparent ${top + height}px, 
          rgba(0,0,0,0.4) ${top + height}px
        ),
        linear-gradient(to right, 
          rgba(0,0,0,0.4) 0%, 
          rgba(0,0,0,0.4) ${left}px, 
          transparent ${left}px, 
          transparent ${left + width}px, 
          rgba(0,0,0,0.4) ${left + width}px
        )
      `,
      backgroundBlendMode: "darken",
      pointerEvents: "auto"
    };
  };

  return (
    <>
      {/* Dark overlay with transparent hole around target - allows scrolling */}
      <div 
        className="fixed inset-0 z-[9999] bg-black/40 pointer-events-none"
        style={{
          clipPath: `polygon(
            0% 0%, 
            0% 100%, 
            ${targetRect.left - 8}px 100%, 
            ${targetRect.left - 8}px ${targetRect.top - 8}px, 
            ${targetRect.right + 8}px ${targetRect.top - 8}px, 
            ${targetRect.right + 8}px ${targetRect.bottom + 8}px, 
            ${targetRect.left - 8}px ${targetRect.bottom + 8}px, 
            ${targetRect.left - 8}px 100%, 
            100% 100%, 
            100% 0%
          )`
        }}
      />
      
      {/* Spotlight border on target element */}
      <div
        className="fixed z-[10000] rounded-lg pointer-events-none"
        style={{
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
          boxShadow: `
            0 0 0 2px #FF6B00,
            0 0 0 4px rgba(255, 107, 0, 0.6),
            0 0 15px 8px rgba(255, 107, 0, 0.4)
          `,
          background: "transparent",
          animation: "spotlight-pulse 1.5s ease-in-out infinite"
        }}
      />
      <style>{`
        @keyframes spotlight-pulse {
          0%, 100% {
            box-shadow: 
              0 0 0 2px #FF6B00,
              0 0 0 4px rgba(255, 107, 0, 0.6),
              0 0 15px 8px rgba(255, 107, 0, 0.4);
          }
          50% {
            box-shadow: 
              0 0 0 4px #FF6B00,
              0 0 0 8px rgba(255, 107, 0, 0.7),
              0 0 25px 12px rgba(255, 107, 0, 0.5);
          }
        }
      `}</style>

      {/* Tooltip */}
      <div
        className="border border-orange-300 rounded-xl shadow-2xl p-4 animate-in fade-in-0 zoom-in-95 duration-200"
        style={{
          ...getTooltipStyle(),
          backgroundColor: "rgba(255, 237, 213, 0.95)"
        }}
      >
        {/* Header with close button */}
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-foreground text-base">{step.title}</h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 -mr-2 -mt-1 text-muted-foreground hover:text-foreground touch-manipulation"
            onClick={handleClose}
            onTouchEnd={handleClose}
            type="button"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Message */}
        <p className="text-sm text-muted-foreground mb-4">{step.message}</p>

        {/* Progress indicator */}
        <div className="flex justify-center gap-1.5 mb-3">
          {tutorialSteps.map((_, index) => (
            <div
              key={index}
              className={cn(
                "w-2 h-2 rounded-full transition-colors",
                index === currentStep 
                  ? "bg-primary" 
                  : index < currentStep 
                    ? "bg-primary/40" 
                    : "bg-muted"
              )}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevious}
            onTouchEnd={handlePrevious}
            className="h-10 w-10 touch-manipulation"
            type="button"
            disabled={isFirstStep}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          
          <Button
            onClick={handleNext}
            onTouchEnd={handleNext}
            className="h-10 px-6 touch-manipulation min-w-[100px]"
            type="button"
          >
            {isLastStep ? "Terminer" : "Suivant"}
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            onTouchEnd={handleNext}
            className="h-10 w-10 touch-manipulation"
            type="button"
            disabled={isLastStep}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Skip link */}
        {!isLastStep && (
          <button
            onClick={handleClose}
            onTouchEnd={handleClose}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground mt-3 py-2 transition-colors touch-manipulation"
            type="button"
          >
            Passer le tutoriel
          </button>
        )}
      </div>
    </>
  );
}
